<template>
  <div class="login-title">{{ t('Create your account') }}</div>
  <register-step :step="3" :count="3" />

  <div class="row-2-fields">
    <ui-select
      :value="form.messengerType"
      :items="messengers"
      :label="t('Messenger')"
      :placeholder="t('Messenger')"
      class="ui-select__login"
      :returnObject="false"
      :autocomplete="false"
      item-value="id"
      :error-messages="fieldErrors.messengerType ? [fieldErrors.messengerType] : []"
      @change="
        form.messengerType = $event;
        clearFieldError('messengerType');
      "
    >
      <template #item="{ item, props: { onClick } }">
        <v-list-item v-bind="item" :title="null" @click="onClick">
          <ui-icon :color="$colors.blue" :icon="item.raw.icon" />
          {{ item.raw.id }}
        </v-list-item>
      </template>
      <template #selection="{ item }">
        <v-list-item v-bind="item" :title="null">
          <ui-icon :color="$colors.blue" :icon="item.raw.icon" />
          {{ item.raw.id }}
        </v-list-item>
      </template>
    </ui-select>

    <ui-input
      v-model="form.messengerAccount"
      class="ui-input__login"
      :disabled="form.messengerType === 'WhatsApp'"
      :label="t('Messenger ID')"
      placeholder="@username"
      name="messengerAccount"
      :error-messages="fieldErrors.messengerAccount ? [fieldErrors.messengerAccount] : []"
      @change="clearFieldError('messengerAccount')"
    />
  </div>

  <ui-phone-input
    v-model="form.phoneNumber"
    :label="t('Phone number') + '*'"
    class="ui-input__login"
    :placeholder="`${t('e.g.')} +1 541 754 3010`"
    @blur="onPhoneBlur(form)"
    @valid="validatePhoneNumber"
    :error-messages="fieldErrors.phoneNumber ? [fieldErrors.phoneNumber] : []"
  />

  <ui-select
    :value="form.currencyId"
    :items="currencyListOrdered"
    :placeholder="t('Select account currency...')"
    item-text="name"
    item-value="code"
    :return-object="false"
    class="ui-select__login"
    :error-messages="fieldErrors.currencyId ? [fieldErrors.currencyId] : []"
    :autocomplete="false"
    @change="
      form.currencyId = $event;
      clearFieldError('currencyId');
    "
  >
    <template #label>
      <b>{{ t('Account currency') }}*</b>
      <ui-tooltip-icon
        preset="help"
        :color="$colors.gray40"
        class="tooltip-icon mb-2 ml-4"
      >
        <div>
          {{
            t(
              'Selected currency will be used to store and display all the financial data in your BeMob account.',
            )
          }}
        </div>
        <div>{{ t("It can't be changed after the registration.") }}</div>
      </ui-tooltip-icon>
    </template>

    <template #item="{ item, props: { onClick } }">
      <v-list-item v-bind="item" :title="null" @click="onClick">
        <span class="currency-sign">{{ item.raw.sign }}</span>
        {{ item.raw.name }}
      </v-list-item>
    </template>
    <template #selection="{ item }">
      <v-list-item v-bind="item" :title="null">
        <span class="currency-sign">{{ item.raw.sign }}</span>
        {{ item.raw.name }}
      </v-list-item>
    </template>
  </ui-select>

  <ui-info color="red" class="mb-24 mt-4">
    {{ t("Currency can't be changed after the registration") }}
  </ui-info>

  <ui-button
    :disabled="!isFormValid(form)"
    :loading="apiLoading"
    button-style="contained"
    @click="submit"
    class="w100"
    :text="t('Create Account')"
    iconAppend="fas fa-check"
  />
  <div class="text-center pt-8">
    {{ t('Have questions?') }}
    <a class="d-inline open-support" target="_blank" @click="openLiveSupport">{{
      t('Contact support')
    }}</a>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import RegisterStep from '@/views/pages/components/register-step.vue';
import { useIntercom, useColors, useI18n, useCurrencyList } from '@/composables';
import { useStore } from '@/store';
import { getDashboardLink } from '@/utils/get-dashboard-link';
import { putUserApiRequest } from '@/services/api/user-api';
import type { UpdateProfileForm } from '@/services/api/user-api';
import i18n from '@/i18n';

const $colors = useColors();
const { t } = useI18n();
const { openLiveSupport } = useIntercom();
const router = useRouter();
const store = useStore();
const { currencyListOrdered } = useCurrencyList();

const apiLoading = ref(false);
const fieldErrors = reactive<Record<string, string>>({});
const isPhoneNumberValid = ref(false);

const clearFieldError = (field: string) => {
  delete fieldErrors[field];
};

const validatePhoneNumber = (isValid: boolean) => {
  isPhoneNumberValid.value = isValid;
};

const onPhoneBlur = (f: UpdateProfileForm) => {
  if (!f.phoneNumber) {
    fieldErrors.phoneNumber = t('{0} is required', [t('Phone Number')]);
  } else {
    clearFieldError('phoneNumber');
  }
};

const isFormValid = (f: UpdateProfileForm): boolean =>
  !!f.currencyId && !!f.phoneNumber && isPhoneNumberValid.value;

const messengers = [
  { id: 'WhatsApp', icon: 'fab fa-whatsapp' },
  { id: 'Telegram', icon: 'fab fa-telegram' },
];

const apiSubmit = async (f: UpdateProfileForm) => {
  apiLoading.value = true;

  try {
    await putUserApiRequest({
      ...f,
      language: i18n.global.locale.value as string,
    });

    store.$patch(state => {
      state.user = { ...(state.user as Record<string, unknown>), ...f };
    });

    router.push(getDashboardLink(store.user));
  } catch (err: unknown) {
    const serverErrors = (
      err as { response?: { data?: { errors?: Record<string, unknown> } } }
    )?.response?.data?.errors;
    if (serverErrors && typeof serverErrors === 'object') {
      Object.keys(serverErrors).forEach(field => {
        const v = serverErrors[field];
        fieldErrors[field] = (Array.isArray(v) ? v[0] : v) as string;
      });
    }
  } finally {
    apiLoading.value = false;
  }
};

const form = reactive({
  currencyId: null as string | null,
  phoneNumber: null as string | null,
  messengerType: null as string | null,
  messengerAccount: null as string | null,
});

const submit = () => apiSubmit(form);
</script>

<style lang="scss" scoped>
.currency-sign {
  color: var(--color-blue);
  margin-right: 4px;
  min-width: 13px;
  font-weight: bold;
}

.open-support {
  cursor: pointer;
  color: var(--color-blue);
}
</style>
